import { create } from 'zustand';
import { get as lodashGet, set as lodashSet, cloneDeep } from 'lodash';


type QueryKeyParams = [string, Record<string, any>, number, null];

export type StableQueryKey = (string | null)[] | QueryKeyParams;

/**
 * Query keys for reporting features within AR Collections
 */
interface ReportingQueryKeys {
    "performance": StableQueryKey;
    'email-queue': StableQueryKey;
    'email-queue-template': StableQueryKey;
    "appeal": StableQueryKey;
    'write-off': StableQueryKey;
    'ledger-report': StableQueryKey;
    'appeal-by-client-id': StableQueryKey;
}

interface QueueQueryKeys {
    'matter-invoices': StableQueryKey;
    "matters": StableQueryKey;
}

/**
 * Query keys for AR Collections feature
 */
interface ArCollectionsQueryKeys {
    queue: QueueQueryKeys;
    reporting: ReportingQueryKeys;
}

/**
 * Root query keys structure for all features
 */
interface QueryKeys {
    'ar-collections': ArCollectionsQueryKeys;
}

/**
 * Store interface for managing React Query keys
 */
interface QueryKeyStore {
    queryKeys: QueryKeys;
    /**
     * Get a query key array from the store using nested path
     * @param feature - Feature name (e.g., 'ar-collections')
     * @param path - Nested path (e.g., 'reporting.appeal')
     * @returns Query key array or empty array if not found
     */
    getQueryKey: (feature: keyof QueryKeys, path: string) => StableQueryKey
    /**
     * Set a query key array in the store using nested path
     * @param feature - Feature name (e.g., 'ar-collections')
     * @param path - Nested path (e.g., 'reporting.appeal')
     * @param value - Query key array to store
     * @example setQueryKey("ar-collections", "reporting.appeal", ["appeals", "123"])
     */
    setQueryKey: (feature: keyof QueryKeys, path: string, value: StableQueryKey) => void;
}

/**
 * Default query keys structure with empty arrays
 */
const defaultQueryKeys: QueryKeys = {
    'ar-collections': {
        queue: {
            'matter-invoices': [],
            "matters": [],
        },
        reporting: {
            "performance": [],
            'email-queue': [],
            'email-queue-template':[],
            "appeal": [],
            'write-off': [],
            'ledger-report': [],
            'appeal-by-client-id': [],
        },
    },
};

/**
 * Zustand store for managing React Query keys across the application
 * Provides methods to get and set query keys using nested paths
 */
export const useQueryKeyStore = create<QueryKeyStore>()(
    (set, get) => ({
        queryKeys: defaultQueryKeys,

        /**
         * Get query key from store using nested path
         * Uses lodash get to safely access nested properties
         */
        getQueryKey: (feature, path) => {
            const state = get();
            const fullPath = `queryKeys.${feature}.${path}`;
            const value = lodashGet(state, fullPath);
            return Array.isArray(value) ? value : [];
        },

        /**
         * Set query key in store using nested path
         * Uses lodash set to safely update nested properties
         * Deep clones state to avoid mutations
         */
        setQueryKey: (feature, path, value) => {
            set((state) => {
                // Deep clone to avoid mutation
                const newState = cloneDeep(state);
                const fullPath = `queryKeys.${feature}.${path}`;

                // Use lodash set to set value at nested path
                lodashSet(newState, fullPath, value);

                return newState;
            });
        },
    })
);
